import { ChevronRight, UserRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserAvatar } from "@/components/ui/user-avatar";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { useI18n } from "@/hooks/useI18n";
import { PaymentStatusCell } from "@/lib/dashboard/shared/PaymentStatusCell";
import { RequestsCountCell } from "@/lib/dashboard/shared/RequestsCountCell";
import { clientDisplayName } from "@/lib/dashboard/clients/client-display";
import { AgencyClientRow } from "@/integrations/supabase/rpc-types";

interface ClientsTableProps {
  rows: AgencyClientRow[];
  /** Riga aperta nel pannello: resta evidenziata sotto lo sheet. */
  selectedKey: string | null;
  onSelect: (row: AgencyClientRow) => void;
  showAdvisorColumn: boolean;
}

export function ClientsTable({
  rows,
  selectedKey,
  onSelect,
  showAdvisorColumn,
}: ClientsTableProps) {
  const { t } = useI18n();
  const dateFormatter = new Intl.DateTimeFormat("it-IT", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("dashboard.overview.clients.columnClient")}</TableHead>
                {showAdvisorColumn && (
                  <TableHead>
                    <span className="inline-flex items-center gap-1.5">
                      {t("dashboard.overview.clients.columnAdvisor")}
                      <InfoTooltip
                        text={t("dashboard.overview.clients.columnAdvisorInfo")}
                      />
                    </span>
                  </TableHead>
                )}
                {/* Una cella sola: vedi RequestsCountCell. */}
                <TableHead className="text-right">
                  <span className="inline-flex items-center gap-1.5">
                    {t("dashboard.overview.requests.columnRequests")}
                    <InfoTooltip
                      text={t("dashboard.overview.clients.columnRequestsInfo")}
                    />
                  </span>
                </TableHead>
                {/* Valore chiuso e residuo erano due colonne di importi da sottrarre a
                    mente: ora sono una cella sola con totale, barra e i due addendi. */}
                <TableHead className="text-right">
                  <span className="inline-flex items-center gap-1.5">
                    {t("dashboard.overview.paymentStatus.column")}
                    <InfoTooltip
                      text={t("dashboard.overview.paymentStatus.infoClients")}
                    />
                  </span>
                </TableHead>
                <TableHead className="text-right">
                  {t("dashboard.overview.clients.columnLastActivity")}
                </TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const name = clientDisplayName(row);
                const advisorName =
                  [row.advisor_first_name, row.advisor_last_name]
                    .filter(Boolean)
                    .join(" ") || null;
                return (
                  <TableRow
                    key={row.client_key}
                    onClick={() => onSelect(row)}
                    // Riga cliccabile ma anche raggiungibile da tastiera: e' l'unico modo
                    // per aprire la scheda, quindi non puo' dipendere dal mouse.
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect(row);
                      }
                    }}
                    className={`cursor-pointer ${
                      selectedKey === row.client_key ? "bg-orange-50/60" : ""
                    }`}
                  >
                    <TableCell className="font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <UserAvatar
                          expanded
                          firstName={row.first_name}
                          lastName={row.last_name}
                          fullName={name}
                          email={row.email}
                          imageUrl={row.avatar_url}
                          size="sm"
                        />
                        {!row.has_account && (
                          <InfoTooltip
                            text={t("dashboard.overview.clients.noAccountInfo")}
                          />
                        )}
                      </div>
                    </TableCell>

                    {showAdvisorColumn && (
                      <TableCell className="text-gray-600 whitespace-nowrap">
                        {advisorName ? (
                          <span className="inline-flex items-center gap-1.5">
                            {advisorName}
                            {/*
                              Un cliente puo' essere passato per piu' consulenti. I numeri
                              della riga li sommano tutti, quindi mostrare un nome solo e
                              tacere sugli altri farebbe attribuire a lui un valore che
                              non e' tutto suo.
                            */}
                            {row.advisor_count > 1 && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal cursor-default"
                                title={row.advisors
                                  .map((a) =>
                                    t("dashboard.overview.clients.advisorRequests", {
                                      name:
                                        [a.first_name, a.last_name]
                                          .filter(Boolean)
                                          .join(" ") || a.advisor_id,
                                      count: a.requests_count,
                                    })
                                  )
                                  .join("\n")}
                              >
                                +{row.advisor_count - 1}
                              </Badge>
                            )}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    )}

                    <TableCell className="text-right">
                      <RequestsCountCell
                        activeCount={row.active_count}
                        completedCount={row.completed_count}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <PaymentStatusCell
                        paidAmount={row.paid_amount}
                        pendingAmount={row.remaining_amount}
                        overdueAmount={row.overdue_amount}
                        totalAmount={row.total_amount}
                      />
                    </TableCell>

                    <TableCell className="text-right text-gray-500 whitespace-nowrap">
                      {dateFormatter.format(new Date(row.last_request_at))}
                    </TableCell>

                    <TableCell className="text-right">
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

interface ClientsEmptyProps {
  filtered: boolean;
}

/** Vuoto per davvero e vuoto per colpa dei filtri sono due messaggi diversi. */
export function ClientsEmpty({ filtered }: ClientsEmptyProps) {
  const { t } = useI18n();

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col items-center gap-2 py-12 text-center text-gray-500">
          <UserRound className="h-8 w-8 text-gray-300" />
          <p className="text-sm">
            {filtered
              ? t("dashboard.overview.clients.emptyFiltered")
              : t("dashboard.overview.clients.empty")}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default ClientsTable;
